import React, { useEffect, useMemo, useRef, useState } from "react";
import { LivePoseFile, frameIndexAt, framePoints, interpolatedFramePoints } from "../live/liveAnalysis";
import { drawSkeleton, SkeletonPoint } from "../pose/skeleton";

/**
 * The uploaded clip playing on a loop with the on-device skeleton drawn over it. The clip is the VISIBLE element and the
 * skeleton a see-through canvas on top (owner, 2026-10-10, Chrome desktop: a display:none clip copied onto a canvas
 * showed one still frame, Chrome pausing a muted autoplay video that is not visible): what the session
 * page shows while the upload is still being processed, so there is the user's own video and the
 * pose found on it to look at instead of a bare spinner. No controls, no metrics — the live free
 * view is the real player once the result is out.
 *
 * Pose frame i sits at t = i / fps from the start of the clip (the split's own time base for an
 * upload sent whole, which is what the upload page sends).
 */
export default function PoseClip({ clipUrl, poseFile }: { clipUrl: string; poseFile: LivePoseFile }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The box is the clip's own shape, at most 20rem tall: a portrait phone clip is not letterboxed into a wide box (which
  // would shrink it to a sliver). The shape is the VIDEO's once it has loaded: the skeleton canvas fills the box, so a
  // box of any other shape stretches it. A pose file's size is only its coordinate space: the GPU pose says 1280x720
  // whatever the clip (owner, 2026-10-28, dev 20003: the skeleton grew past a portrait clip near the end of processing).
  // Until the video loads, the pose file's shape stands in.
  const [videoAspect, setVideoAspect] = useState<number | null>(null);
  const aspect =
    videoAspect ?? (poseFile.captureWidth > 0 && poseFile.captureHeight > 0 ? poseFile.captureWidth / poseFile.captureHeight : 16 / 9);
  const frames = useMemo(() => (poseFile.frames ?? []).map(framePoints), [poseFile]);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const g = canvas?.getContext?.("2d");
      if (video && canvas && g && video.videoWidth > 0) {
        if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
        if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;
        g.clearRect(0, 0, canvas.width, canvas.height);
        const fps = poseFile.fps > 0 ? poseFile.fps : 10;
        const at = video.currentTime * fps;
        // Pose coordinates are in the capture's pixels; the canvas is the video's own size.
        const sx = poseFile.captureWidth > 0 ? canvas.width / poseFile.captureWidth : 1;
        const sy = poseFile.captureHeight > 0 ? canvas.height / poseFile.captureHeight : 1;
        const points = new Map<string, SkeletonPoint>();
        if (at <= frames.length - 1 + 0.5) {
          const source = frames.length > 1 ? interpolatedFramePoints(frames, at) : frames[frameIndexAt(video.currentTime, fps, frames.length)];
          source?.forEach((p, name) => points.set(name, { x: p.x * sx, y: p.y * sy }));
        }
        drawSkeleton(g, points, canvas.width, { displayWidth: canvas.getBoundingClientRect().width });
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [frames, poseFile]);

  return (
    <div className="w-full max-w-md" data-testid="pose-clip">
      <div
        className="relative mx-auto overflow-hidden rounded-lg bg-black"
        style={{ width: `min(100%, calc(20rem * ${aspect}))`, aspectRatio: String(aspect) }}
      >
        <video
          ref={videoRef}
          src={clipUrl}
          className="block h-full w-full object-contain"
          muted
          playsInline
          autoPlay
          loop
          preload="auto"
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (v.videoWidth > 0 && v.videoHeight > 0) setVideoAspect(v.videoWidth / v.videoHeight);
          }}
          onLoadedData={(e) => { e.currentTarget.play?.()?.catch?.(() => undefined); }}
        />
        <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
      </div>
      <div className="mt-1 text-xs text-slate-500">Your clip, with the pose found on this device.</div>
    </div>
  );
}
